import React, { useEffect, useRef, useState } from 'react';

const SECTION_HEIGHT = 720;
const PARALLAX_INTENSITY = 0.35;

const ParallaxBanner: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [offsetY, setOffsetY] = useState(0);
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile on mount and on resize
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Parallax scroll — desktop only
  useEffect(() => {
    if (isMobile) return;

    const BUFFER = Math.ceil((window.innerHeight / 2 + SECTION_HEIGHT) * PARALLAX_INTENSITY) + 20;

    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      if (rect.bottom >= 0 && rect.top <= windowHeight) {
        const relativeScroll = rect.top - windowHeight / 2;
        setOffsetY(relativeScroll * PARALLAX_INTENSITY);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Content reveal via IntersectionObserver
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const BUFFER = typeof window !== 'undefined'
    ? Math.ceil((window.innerHeight / 2 + SECTION_HEIGHT) * PARALLAX_INTENSITY) + 20
    : 200;

  // Shared text content — same markup for both mobile and desktop
  const textContent = (isVisible: boolean) => (
    <>
      <div className={`pb-ornament ${isVisible ? 'pb--visible' : ''}`}>
        <div className="pb-ornament-line" />
        <div className="pb-ornament-diamond" />
        <div className="pb-ornament-line" />
      </div>

      <p
        className={`pb-eyebrow ${isVisible ? 'pb--visible' : ''}`}
        style={{
          letterSpacing: '0.3em', textTransform: 'uppercase',
          color: '#ffffff', fontSize: '11px',
          marginBottom: '14px', fontFamily: 'inherit',
        }}
      >
        New Collection
      </p>

      <div className={`pb-line ${isVisible ? 'pb--visible' : ''}`} />

      <h2
        className={`pb-title ${isVisible ? 'pb--visible' : ''}`}
        style={{
          color: '#fff',
          fontSize: 'clamp(2.2rem, 5vw, 4rem)',
          fontWeight: 700, lineHeight: 1.1,
          marginBottom: '20px', fontFamily: 'inherit',
        }}
      >
        Discover Timeless Elegance
      </h2>

      <p
        className={`pb-body ${isVisible ? 'pb--visible' : ''}`}
        style={{
          color: 'rgba(255,255,255,0.8)',
          fontSize: '1rem', maxWidth: '500px',
          lineHeight: 1.8, fontFamily: 'inherit',
        }}
      >
        Explore our curated collection of fine jewellery, crafted with precision and designed to shine through every moment.
      </p>

      <div className={`pb-divider ${isVisible ? 'pb--visible' : ''}`} />

      <a href="/shop" className={`pb-btn pb-cta ${isVisible ? 'pb--visible' : ''}`}>
        <span>Shop the Collection</span>
      </a>
    </>
  );

  return (
    <div ref={sectionRef} style={{ position: 'relative', width: '100%' }}>
      <style>{`
        /* ── Content animations ── */
        .pb-eyebrow {
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s;
        }
        .pb-eyebrow.pb--visible { opacity: 1; transform: translateY(0); }

        .pb-line {
          width: 0;
          height: 1px;
          background: rgba(255,255,255,0.4);
          margin: 0 auto 28px;
          transition: width 1s cubic-bezier(0.16,1,0.3,1) 0.25s;
        }
        .pb-line.pb--visible { width: 80px; }

        .pb-title {
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.85s cubic-bezier(0.16,1,0.3,1) 0.35s,
                      transform 0.85s cubic-bezier(0.16,1,0.3,1) 0.35s;
        }
        .pb-title.pb--visible { opacity: 1; transform: translateY(0); }

        .pb-body {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.8s ease 0.55s, transform 0.8s ease 0.55s;
        }
        .pb-body.pb--visible { opacity: 1; transform: translateY(0); }

        .pb-divider {
          width: 0;
          height: 1px;
          background: rgba(255,255,255,0.25);
          margin: 0 auto 36px;
          transition: width 1s cubic-bezier(0.16,1,0.3,1) 0.65s;
        }
        .pb-divider.pb--visible { width: 120px; }

        .pb-cta {
          opacity: 0;
          transform: translateY(14px) scale(0.97);
          transition: opacity 0.7s ease 0.8s,
                      transform 0.7s cubic-bezier(0.34,1.56,0.64,1) 0.8s;
        }
        .pb-cta.pb--visible { opacity: 1; transform: translateY(0) scale(1); }

        /* ── Ornament lines ── */
        .pb-ornament {
          display: flex;
          align-items: center;
          gap: 14px;
          justify-content: center;
          margin-bottom: 28px;
          opacity: 0;
          transition: opacity 0.6s ease 0.2s;
        }
        .pb-ornament.pb--visible { opacity: 1; }
        .pb-ornament-line {
          width: 0;
          height: 1px;
          background: rgba(255,255,255,0.45);
          transition: width 0.9s cubic-bezier(0.16,1,0.3,1) 0.3s;
        }
        .pb-ornament.pb--visible .pb-ornament-line { width: 48px; }
        .pb-ornament-diamond {
          width: 6px; height: 6px;
          background: #717fe0;
          transform: rotate(45deg) scale(0);
          flex-shrink: 0;
          transition: transform 0.45s cubic-bezier(0.34,1.56,0.64,1) 0.55s;
        }
        .pb-ornament.pb--visible .pb-ornament-diamond { transform: rotate(45deg) scale(1); }

        /* ── CTA button ── */
        .pb-btn {
          display: inline-block;
          padding: 15px 48px;
          background: transparent;
          color: #fff;
          text-decoration: none;
          text-transform: uppercase;
          letter-spacing: 0.18em;
          font-size: 12px;
          font-weight: 600;
          border: 1px solid rgba(255,255,255,0.7);
          transition: background 0.35s ease, color 0.35s ease, border-color 0.35s ease,
                      transform 0.3s ease, box-shadow 0.3s ease;
          font-family: inherit;
          position: relative;
          overflow: hidden;
        }
        .pb-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: #fff;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.35s cubic-bezier(0.16,1,0.3,1);
          z-index: 0;
        }
        .pb-btn span { position: relative; z-index: 1; }
        .pb-btn:hover::before { transform: scaleX(1); }
        .pb-btn:hover { color: #222; border-color: #fff; box-shadow: 0 8px 24px rgba(0,0,0,0.25); }
        .pb-btn:active { transform: scale(0.97); }

        /* ── DESKTOP wrapper ── */
        .pb-desktop-wrapper {
          position: relative;
          width: 100%;
          height: ${SECTION_HEIGHT}px;
          overflow: hidden;
        }

        /* ── MOBILE wrapper: portrait image, full height, no cropping ── */
        .pb-mobile-wrapper {
          position: relative;
          width: 100%;
          display: block;
          background-color: #111;
        }

        /* Portrait image: full width, auto height = whole image visible */
        .pb-mobile-wrapper .pb-img {
          display: block;
          width: 100%;
          height: auto;
        }

        /* Overlay for text readability */
        .pb-mobile-wrapper .pb-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(160deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.60) 100%);
        }

        /* Content centered over the portrait image */
        .pb-mobile-wrapper .pb-content {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 20px;
        }

        /* Mobile: show all content immediately, no animation delays */
        @media (max-width: 768px) {
          .pb-eyebrow,
          .pb-title,
          .pb-body,
          .pb-cta,
          .pb-ornament {
            opacity: 1 !important;
            transform: none !important;
            transition: none !important;
          }
          .pb-line,
          .pb-divider          { width: 80px !important; transition: none !important; }
          .pb-ornament-line    { width: 48px !important; transition: none !important; }
          .pb-ornament-diamond { transform: rotate(45deg) scale(1) !important; transition: none !important; }

          .pb-title { font-size: clamp(1.6rem, 6vw, 2.4rem) !important; }
          .pb-body  { font-size: 0.9rem !important; }
          .pb-btn   { padding: 12px 32px !important; }
        }
      `}</style>

      {isMobile ? (
        /* ── MOBILE: portrait <img>, full height, no cropping ── */
        <div className="pb-mobile-wrapper">
          <img
            src="asset/images/slide-10-potraite.png"
            alt="Discover Your Style"
            className="pb-img"
          />
          <div className="pb-overlay" />
          <div className="pb-content">
            {textContent(true)}
          </div>
        </div>
      ) : (
        /* ── DESKTOP: parallax background-image, fixed height, unchanged ── */
        <div className="pb-desktop-wrapper">
          {/* Parallax background */}
          <div
            style={{
              position: 'absolute',
              top: `-${BUFFER}px`,
              bottom: `-${BUFFER}px`,
              left: 0, right: 0,
              backgroundImage: `url('asset/images/slide-10.png')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundAttachment: 'scroll',
              transform: `translateY(${offsetY}px)`,
              willChange: 'transform',
            }}
          />
          {/* Dark overlay */}
          <div
            style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(160deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)',
            }}
          />
          {/* Content */}
          <div
            style={{
              position: 'relative', zIndex: 4,
              height: '100%',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              textAlign: 'center', padding: '0 20px',
            }}
          >
            {textContent(visible)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ParallaxBanner;